import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { logoutIfNeeded } from "common/utils";
import {
  getPersistedProcessingGigPayouts,
  persistProcessingGigPayouts,
  removePersistedProcessingGigPayouts,
} from "services/PersistService/PersistGigPayoutsService";
import {
  getPersistedProcessingEarningPayouts,
  persistProcessingEarningPayouts,
  removePersistedProcessingEarningPayouts,
} from "services/PersistService/PersistEarningPayoutsService";
import {
  getPersistedProcessingAlterEarnings,
  persistProcessingAlterEarnings,
  removePersistedProcessingAlterEarnings,
} from "services/PersistService/PersistAlterEarningsService";
import { RootState } from "store/store";

export interface IGigWorkerPayout {
  payoutId: number;
  userId: number;
  mysaPartnerId: string | null;
  payoutMonth: string;
  totalAmount: number;
  status: string;
  mysaExternalId: string;
  mysaRaisedOn: string | null;
}

export interface IGigWorkerEarning {
  userId: number;
  mysaAccountStatus: string;
  mysaPartnerId: string | null;
  name: string | null;
  mobile: string | null;
  earnings: number;
  earningDays: number;
  alteredEarnings: number | null;
  payableEarnings: number;
  paidAmount: number;
  pendingAmount: number;
}

export interface IGigPayoutsUpload {
  key?: string;
  headerMapping?: string;
  successRows?: any[];
  failedRows?: any[];
  timeUploaded?: string;
  [key: string]: any;
}

export interface PartnerServicesState {
  earnings: IGigWorkerEarning[];
  earningPayouts: IGigWorkerEarning[];
  payouts: IGigWorkerPayout[];
  processingGigPayouts: IGigPayoutsUpload;
  processingEarningPayouts: IGigPayoutsUpload;
  processingAlterEarnings: IGigPayoutsUpload;
  status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving" | "downloading";
  payoutsStatus: "idle" | "loading" | "error" | "processing";
  earningPayoutsDownloadStatus: "idle" | "downloading" | "error";
  onboardingDownloadStatus: "idle" | "downloading" | "error";
  error?: string;
  payoutsError?: string;
}

const initialState: PartnerServicesState = {
  earnings: [],
  earningPayouts: [],
  payouts: [],
  processingGigPayouts: getPersistedProcessingGigPayouts(),
  processingEarningPayouts: getPersistedProcessingEarningPayouts(),
  processingAlterEarnings: getPersistedProcessingAlterEarnings(),
  status: "idle",
  payoutsStatus: "idle",
  earningPayoutsDownloadStatus: "idle",
  onboardingDownloadStatus: "idle",
};

export const getGigWorkerEarnings = createAsyncThunk(
  "partnerServices/getGigWorkerEarnings",
  async (params: { startDate: string; endDate: string }, thunkApi) => {
    try {
      const response = await API.get(apis.gigWorkerEarnings, { params });
      return response.data as IGigWorkerEarning[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const downloadGigWorkerEarnings = createAsyncThunk(
  "partnerServices/downloadGigWorkerEarnings",
  async (params: { startDate: string; endDate: string }, thunkApi) => {
    try {
      const response = await API.get(apis.gigWorkerEarningsDownload, { params });
      const downloadUrl: string = response.data?.url;
      if (!downloadUrl) throw new Error("Download URL not found in response");
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const downloadGigWorkerOnboarding = createAsyncThunk(
  "partnerServices/downloadGigWorkerOnboarding",
  async (_, thunkApi) => {
    try {
      const response = await API.get(apis.gigWorkerOnboardingDownload);
      const downloadUrl: string = response.data?.url;
      if (!downloadUrl) throw new Error("Download URL not found in response");
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const saveProcessingGigPayouts = createAsyncThunk(
  "partnerServices/gigPayouts/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.partnerServices.processingGigPayouts;
    try {
      const response = await API.post(apis.saveGigPayouts, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const cancelProcessingGigPayouts = createAsyncThunk(
  "partnerServices/gigPayouts/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.partnerServices.processingGigPayouts;
    try {
      const response = await API.post(apis.saveGigPayouts, {}, { params: { key, cancel: 1 } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getGigWorkerPayouts = createAsyncThunk(
  "partnerServices/getGigWorkerPayouts",
  async (params: { month?: string; status?: string }, thunkApi) => {
    try {
      const response = await API.get(apis.gigWorkerPayouts, { params });
      return response.data as IGigWorkerPayout[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const processGigWorkerPayouts = createAsyncThunk(
  "partnerServices/processGigWorkerPayouts",
  async (
    payload: { month: string; action: "PUSH" | "DISCARD"; payoutIds: number[] },
    thunkApi
  ) => {
    try {
      const response = await API.post(apis.processGigWorkerPayouts, payload);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const markSuccessGigWorkerPayouts = createAsyncThunk(
  "partnerServices/markSuccessGigWorkerPayouts",
  async (payload: { month: string; payoutIds: number[] }, thunkApi) => {
    try {
      const response = await API.post(apis.markSuccessGigWorkerPayouts, payload);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const getEarningPayouts = createAsyncThunk(
  "partnerServices/getEarningPayouts",
  async (params: { month?: string }, thunkApi) => {
    try {
      const response = await API.get(apis.gigWorkerEarnings, { params });
      return response.data as IGigWorkerEarning[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const downloadEarningPayouts = createAsyncThunk(
  "partnerServices/downloadEarningPayouts",
  async (params: { month?: string }, thunkApi) => {
    try {
      const response = await API.get(apis.gigWorkerEarningsDownload, { params });
      const downloadUrl: string = response.data?.url;
      if (!downloadUrl) throw new Error("Download URL not found in response");
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const saveProcessingEarningPayouts = createAsyncThunk(
  "partnerServices/earningPayouts/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.partnerServices.processingEarningPayouts;
    try {
      const response = await API.post(apis.saveGigPayouts, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const cancelProcessingEarningPayouts = createAsyncThunk(
  "partnerServices/earningPayouts/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.partnerServices.processingEarningPayouts;
    try {
      const response = await API.post(apis.saveGigPayouts, {}, { params: { key, cancel: 1 } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const saveProcessingAlterEarnings = createAsyncThunk(
  "partnerServices/alterEarnings/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.partnerServices.processingAlterEarnings;
    try {
      const response = await API.post(apis.saveEarningsAlteration, {}, { params: { key } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const cancelProcessingAlterEarnings = createAsyncThunk(
  "partnerServices/alterEarnings/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { key } = state.partnerServices.processingAlterEarnings;
    try {
      const response = await API.post(apis.saveEarningsAlteration, {}, { params: { key, cancel: 1 } });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

const partnerServicesSlice = createSlice({
  name: "partnerServices",
  initialState,
  reducers: {
    setProcessingGigPayouts: (state, action) => {
      state.processingGigPayouts = { ...action.payload, timeUploaded: new Date().toISOString() };
      state.status = "idle";
      persistProcessingGigPayouts(state.processingGigPayouts);
    },
    removeProcessingGigPayouts: (state) => {
      state.processingGigPayouts = {};
      state.status = "idle";
      removePersistedProcessingGigPayouts();
    },
    setProcessingEarningPayouts: (state, action) => {
      state.processingEarningPayouts = { ...action.payload, timeUploaded: new Date().toISOString() };
      state.status = "idle";
      persistProcessingEarningPayouts(state.processingEarningPayouts);
    },
    removeProcessingEarningPayouts: (state) => {
      state.processingEarningPayouts = {};
      state.status = "idle";
      removePersistedProcessingEarningPayouts();
    },
    setProcessingAlterEarnings: (state, action) => {
      state.processingAlterEarnings = { ...action.payload, timeUploaded: new Date().toISOString() };
      state.status = "idle";
      persistProcessingAlterEarnings(state.processingAlterEarnings);
    },
    removeProcessingAlterEarnings: (state) => {
      state.processingAlterEarnings = {};
      state.status = "idle";
      removePersistedProcessingAlterEarnings();
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getGigWorkerEarnings.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(getGigWorkerEarnings.fulfilled, (state, action) => {
        state.status = "success";
        state.earnings = action.payload;
      })
      .addCase(getGigWorkerEarnings.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })
      .addCase(downloadGigWorkerEarnings.pending, (state) => {
        state.status = "downloading";
        state.error = undefined;
      })
      .addCase(downloadGigWorkerEarnings.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(downloadGigWorkerEarnings.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })
      .addCase(downloadGigWorkerOnboarding.pending, (state) => {
        state.onboardingDownloadStatus = "downloading";
      })
      .addCase(downloadGigWorkerOnboarding.fulfilled, (state) => {
        state.onboardingDownloadStatus = "idle";
      })
      .addCase(downloadGigWorkerOnboarding.rejected, (state) => {
        state.onboardingDownloadStatus = "error";
      })
      .addCase(saveProcessingGigPayouts.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveProcessingGigPayouts.fulfilled, (state) => {
        state.status = "success";
        state.processingGigPayouts = {};
        removePersistedProcessingGigPayouts();
      })
      .addCase(saveProcessingGigPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })
      .addCase(cancelProcessingGigPayouts.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelProcessingGigPayouts.fulfilled, (state) => {
        state.status = "idle";
        state.processingGigPayouts = {};
        removePersistedProcessingGigPayouts();
      })
      .addCase(cancelProcessingGigPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(getEarningPayouts.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(getEarningPayouts.fulfilled, (state, action) => {
        state.status = "idle";
        state.earningPayouts = action.payload;
      })
      .addCase(getEarningPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(downloadEarningPayouts.pending, (state) => {
        state.earningPayoutsDownloadStatus = "downloading";
      })
      .addCase(downloadEarningPayouts.fulfilled, (state) => {
        state.earningPayoutsDownloadStatus = "idle";
      })
      .addCase(downloadEarningPayouts.rejected, (state) => {
        state.earningPayoutsDownloadStatus = "error";
      })

      .addCase(saveProcessingEarningPayouts.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveProcessingEarningPayouts.fulfilled, (state) => {
        state.status = "success";
        state.processingEarningPayouts = {};
        removePersistedProcessingEarningPayouts();
      })
      .addCase(saveProcessingEarningPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(cancelProcessingEarningPayouts.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelProcessingEarningPayouts.fulfilled, (state) => {
        state.status = "idle";
        state.processingEarningPayouts = {};
        removePersistedProcessingEarningPayouts();
      })
      .addCase(cancelProcessingEarningPayouts.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(getGigWorkerPayouts.pending, (state) => {
        state.payoutsStatus = "loading";
        state.payoutsError = undefined;
      })
      .addCase(getGigWorkerPayouts.fulfilled, (state, action) => {
        state.payoutsStatus = "idle";
        state.payouts = action.payload;
      })
      .addCase(getGigWorkerPayouts.rejected, (state, action) => {
        state.payoutsStatus = "error";
        state.payoutsError = (action.payload as { message: string })?.message;
      })

      .addCase(processGigWorkerPayouts.pending, (state) => {
        state.payoutsStatus = "processing";
        state.payoutsError = undefined;
      })
      .addCase(processGigWorkerPayouts.fulfilled, (state) => {
        state.payoutsStatus = "idle";
      })
      .addCase(processGigWorkerPayouts.rejected, (state, action) => {
        state.payoutsStatus = "error";
        state.payoutsError = (action.payload as { message: string })?.message;
      })

      .addCase(markSuccessGigWorkerPayouts.pending, (state) => {
        state.payoutsStatus = "processing";
        state.payoutsError = undefined;
      })
      .addCase(markSuccessGigWorkerPayouts.fulfilled, (state) => {
        state.payoutsStatus = "idle";
      })
      .addCase(markSuccessGigWorkerPayouts.rejected, (state, action) => {
        state.payoutsStatus = "error";
        state.payoutsError = (action.payload as { message: string })?.message;
      })

      .addCase(saveProcessingAlterEarnings.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveProcessingAlterEarnings.fulfilled, (state) => {
        state.status = "success";
        state.processingAlterEarnings = {};
        removePersistedProcessingAlterEarnings();
      })
      .addCase(saveProcessingAlterEarnings.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      })

      .addCase(cancelProcessingAlterEarnings.pending, (state) => {
        state.status = "cancelling";
      })
      .addCase(cancelProcessingAlterEarnings.fulfilled, (state) => {
        state.status = "idle";
        state.processingAlterEarnings = {};
        removePersistedProcessingAlterEarnings();
      })
      .addCase(cancelProcessingAlterEarnings.rejected, (state, action) => {
        state.status = "error";
        state.error = (action.payload as { message: string })?.message;
      });
  },
});

export const {
  setProcessingGigPayouts,
  removeProcessingGigPayouts,
  setProcessingEarningPayouts,
  removeProcessingEarningPayouts,
  setProcessingAlterEarnings,
  removeProcessingAlterEarnings,
} = partnerServicesSlice.actions;
export default partnerServicesSlice.reducer;
